import styled from '@emotion/styled';
import { Theme } from '../../context/PermissionsContext/ThemeContext/themesTypes';

export const Title = styled.div`
  font-weight: 700;
  font-size: 40px;
  margin-bottom: 30px;
  font-family: Space Grotesk;
`;

export const ViewContainer = styled.div`
  height: 100vh;
  max-height: 100vh;
  padding: 125px 100px 20px 100px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 125px 50px 20px 50px;
  }
`;

export const BreadcrumbContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
`;

export const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

//   make border go inside and not outside the box
export const Container = styled.div`
  width: 100%;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 65vh;
  border-radius: 10px;
  position: relative;
  border: 1px solid rgb(238, 238, 238);

  /* Style scrollbars */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #fff3e6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff8533;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #ff6a00;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #ff8533 #fff3e6;
`;

export const TableContainer = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  min-width: 100%;
  width: max-content;
  position: relative;
`;

export const TableHeadCell = styled.th<{ theme?: { custom: Theme } }>`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
  padding: 20px 20px;
  color: #fff;
  z-index: 2;
  white-space: nowrap;

  @media (min-width: 2560px) {
    padding: 20px 100px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

export const StickyTableHeadCell = styled.th<{
  left: number;
  theme?: { custom: Theme };
}>`
  position: sticky;
  top: 0;
  left: ${({ left }) => `${left}px`};
  background-color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
  padding: 10px 20px;
  color: #fff;
  z-index: 3;
  width: ${({ left }) => (left === 0 ? '80px' : '200px')};
  min-width: ${({ left }) => (left === 0 ? '80px' : '200px')};

  @media (min-width: 2560px) {
    padding: 20px 100px;
  }
`;

export const TableBodyRow = styled.tr<{ theme?: { custom: Theme } }>`
  background-color: ${({ theme }) => theme?.custom?.background1 || '#fff'};
  text-align: center;

  &:nth-of-type(even) {
    background-color: ${({ theme }) => theme?.custom?.background3 || '#f0f0f3'};
  }
`;

export const TableBodyCell = styled.td`
  padding: 20px;
  white-space: nowrap;

  @media (min-width: 2560px) {
    padding: 20px 100px;
  }
`;

export const StickyTableBodyCell = styled.td<{
  left: number;
  isEven: boolean;
  theme?: { custom: Theme };
}>`
  position: sticky;
  left: ${({ left }) => `${left}px`};
  padding: 20px;
  background-color: ${({ isEven, theme }) =>
    isEven
      ? theme?.custom?.background1 || '#fff'
      : theme?.custom?.background3 || '#F0F0F3'};
  z-index: 1;
  width: ${({ left }) => (left === 0 ? '80px' : '200px')};
  min-width: ${({ left }) => (left === 0 ? '80px' : '200px')};

  @media (min-width: 2560px) {
    padding: 20px 100px;
  }
`;

export const CustomSelect = styled.select<{ theme?: { custom: Theme } }>`
  border: 1px solid ${({ theme }) => theme?.custom?.stroke || '#d9d9d9'};
  border-radius: 10px;
  padding: 10px 30px 10px 10px !important;
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  background-size: 16px;
  background-image: url('/images/select-dropdown.svg');
  background-repeat: no-repeat;
  background-position: calc(100% - 8px) center;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#fff'};
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  z-index: 10;

  :focus {
    outline: none;
    border-color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
  }

  option {
    background-color: ${({ theme }) => theme?.custom?.background2 || '#fff'};
    color: ${({ theme }) => theme?.custom?.text || '#333'};
  }
`;

export const CustomSelectOption = styled.option<{ theme?: { custom: Theme } }>`
  font-family: Manrope;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  background-color: ${({ theme }) => theme?.custom?.background2 || '#fff'};
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

export const CustomDateInput = styled.input<{ theme?: { custom: Theme } }>`
  border: 1px solid ${({ theme }) =>
    theme?.custom?.stroke || '#d9d9d9'} !important;
  border-radius: 10px;
  padding: 10px 30px 10px 10px !important;
  flex: 1;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#fff'};
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  z-index: 10;
  font-family: Manrope;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color-scheme: ${({ theme }) => (theme?.custom?.isDark ? 'dark' : 'light')};

  &:focus {
    border-color: ${({ theme }) =>
      theme?.custom?.primary || '#ff6a00'} !important;
    outline: none;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: invert(1)
            brightness(50%)
            saturate(10000%)
            hue-rotate(180deg);
`;
